import React, { useState, useEffect, useMemo, useRef } from 'react';

/**
 * Checkbox dropdown for picking several entities at once, used by the Field
 * Intelligence Entity Explorer.
 *
 * A plain <select multiple> was the cheaper option and was rejected: it needs
 * ctrl/cmd-click to add a second item, silently wipes the whole selection on a
 * stray plain click, and cannot be searched. The Brand Manager's actual task is
 * gathering a handful of near-identical names out of a long list (the four
 * Alternaria diseases, a product and its misspellings), which is exactly what
 * that control is worst at.
 *
 * Selections show as removable chips below the trigger, matching the chip idiom
 * the Trend Comparison panel on the same page already uses.
 *
 * Props:
 *   options     — [{ entity_name, entity_type, mention_count, is_group }]
 *                 is_group marks a container: selecting it means everything inside.
 *   selected    — array of entity_name strings
 *   onChange    — (names[]) => void
 *   max         — ceiling on how many can be picked
 *   placeholder — trigger text when nothing is selected
 */
const s = {
  wrap: { position: 'relative', minWidth: '260px' },
  trigger: {
    padding: '8px 12px', border: '1px solid var(--card-border)', borderRadius: '10px',
    background: 'var(--card-bg)', color: 'var(--text-dark)', fontSize: '14px',
    fontFamily: 'inherit', cursor: 'pointer', minWidth: '260px', textAlign: 'left',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
  },
  menu: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 30, minWidth: '300px',
    background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
  },
  search: {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: '13px',
    fontFamily: 'inherit', border: 'none', borderBottom: '1px solid var(--card-border)',
    background: 'var(--card-bg)', color: 'var(--text-dark)', outline: 'none',
  },
  list: { maxHeight: '260px', overflowY: 'auto' },
  row: {
    display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 12px',
    fontSize: '13px', cursor: 'pointer', color: 'var(--text-dark)',
  },
  count: { marginLeft: 'auto', fontSize: '11px', color: 'var(--text-lighter)' },
  type: { fontSize: '11px', color: 'var(--text-lighter)' },
  hint: { padding: '10px 12px', fontSize: '12px', color: 'var(--text-lighter)' },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 12px', borderTop: '1px solid var(--card-border)', fontSize: '12px',
    color: 'var(--text-lighter)',
  },
  link: {
    background: 'none', border: 'none', padding: 0, fontFamily: 'inherit',
    fontSize: '12px', fontWeight: 600, color: 'var(--text-light)', cursor: 'pointer',
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' },
  chip: {
    padding: '4px 12px', fontSize: '12px', fontWeight: 600, background: 'var(--bg-dark)',
    border: '1px solid var(--card-border)', borderRadius: '16px', cursor: 'pointer',
    color: 'var(--text-light)', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: '6px',
  },
};

export default function EntityMultiSelect({ options = [], selected = [], onChange, max = 25, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  // Curated group narrowing, layered above the free-text search rather than replacing it.
  // The two do different jobs: search finds a name you can already spell, the group filter
  // answers "show me the fungicides" — which is the question that has no answer otherwise,
  // since every product carries the same entity_type.
  const [group, setGroup] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  // Reset the search when the menu closes, so reopening never starts on a stale
  // term that hides everything the user is looking for.
  useEffect(() => { if (!open) { setQ(''); setGroup(''); } }, [open]);

  // Groups present in the current options, so the row only ever offers a filter that
  // will actually match something. group_names arrives comma-joined from the backend
  // (SQLite cannot use a custom separator with DISTINCT) and is absent on a pre-026 DB,
  // in which case the row does not render at all.
  // Ordered by how much conversation sits behind each group, not alphabetically: the
  // busiest group is what someone is nearly always after, and an A-Z list buries it
  // among ones with a handful of mentions.
  const groups = useMemo(() => {
    const weight = new Map();
    options.forEach(o => (o.group_names || '').split(',')
      .map(g => g.trim()).filter(Boolean)
      .forEach(g => weight.set(g, (weight.get(g) || 0) + (o.mention_count || 0))));
    return [...weight.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name]) => name);
  }, [options]);

  const term = q.trim().toLowerCase();
  const visible = useMemo(() => {
    let out = options;
    if (group) {
      out = out.filter(o => (o.group_names || '').split(',')
        .map(g => g.trim()).includes(group));
    }
    return term ? out.filter(o => o.entity_name.toLowerCase().includes(term)) : out;
  }, [options, term, group]);

  const atMax = selected.length >= max;
  const toggle = (name) => {
    if (selected.includes(name)) onChange(selected.filter(n => n !== name));
    else if (!atMax) onChange([...selected, name]);
  };

  const label = selected.length === 0
    ? (placeholder || '— select entities —')
    : selected.length === 1
      ? selected[0]
      : `${selected[0]} + ${selected.length - 1} more`;

  return (
    <div ref={wrapRef} style={s.wrap}>
      <button
        type="button"
        style={s.trigger}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        /* Name carries purpose AND state: a bare "Select entities" would hide the
           current selection from a screen reader, which is the only thing the
           sighted user reads off this button. */
        aria-label={selected.length ? `Select entities, ${label}` : 'Select entities'}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ fontSize: '10px', color: 'var(--text-lighter)' }}>▼</span>
      </button>

      {open && (
        <div style={s.menu}>
          <input
            autoFocus
            style={s.search}
            placeholder="Search entities…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          {groups.length > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '8px 12px',
              borderBottom: '1px solid var(--card-border)',
            }}>
              {['All', ...groups].map(g => {
                const value = g === 'All' ? '' : g;
                const active = group === value;
                return (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGroup(value)}
                    aria-pressed={active}
                    style={{
                      padding: '3px 10px', fontSize: '11px', fontWeight: 600,
                      borderRadius: '12px', cursor: 'pointer', fontFamily: 'inherit',
                      border: '1px solid var(--card-border)',
                      background: active ? 'var(--text-dark)' : 'var(--bg-dark)',
                      color: active ? 'var(--card-bg)' : 'var(--text-light)',
                    }}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
          )}
          <div style={s.list}>
            {options.length === 0 ? (
              <div style={s.hint}>No entities with data in the current filters.</div>
            ) : visible.length === 0 ? (
              /* The group filter can empty the list on its own, so naming only the search
                 term would blame the wrong control — and leave an empty quoted string. */
              <div style={s.hint}>
                {term && group ? `No ${group} matches for “${q.trim()}”`
                  : term ? `No matches for “${q.trim()}”`
                    : `No entities in ${group} with data in the current filters.`}
              </div>
            ) : (
              visible.map(o => {
                const checked = selected.includes(o.entity_name);
                // Disable only unchecked rows at the cap, so the user can always
                // untick their way back down.
                const disabled = atMax && !checked;
                return (
                  <label
                    key={`${o.entity_type}|${o.entity_name}`}
                    style={{ ...s.row, opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'var(--bg-dark)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(o.entity_name)}
                      style={{ cursor: 'inherit' }}
                    />
                    <span>{o.entity_name}</span>
                    {/* A group stands for everything inside it, so say so rather than
                        showing its type — "Fungicide · Ingredient Type" reads like a
                        stray entity, "Fungicide · group of 10" reads like what it is. */}
                    {o.is_group ? (
                      <span style={{ ...s.type, fontWeight: 600 }}>· group</span>
                    ) : (
                      <span style={s.type}>· {o.entity_type}</span>
                    )}
                    {o.mention_count != null && <span style={s.count}>{o.mention_count}</span>}
                  </label>
                );
              })
            )}
          </div>
          <div style={s.footer}>
            <span>{selected.length} selected{atMax ? ` · limit ${max}` : ''}</span>
            {selected.length > 0 && (
              <button type="button" style={s.link} onClick={() => onChange([])}>Clear all</button>
            )}
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <div style={s.chips}>
          {selected.map(name => (
            <button
              key={name}
              type="button"
              onClick={() => onChange(selected.filter(n => n !== name))}
              title={`Remove ${name}`}
              style={s.chip}
            >
              {name} <span style={{ fontSize: '14px', color: 'var(--text-lighter)' }}>×</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
